<script module lang="ts">
  import { dieCollection } from '../../models/die.type';
  import { techniques } from '../../models/techniques.type';

  /**
   * Built once for the whole section rather than once per row. Every face wants
   * to know where it turns up, and rescanning 62 dice and every technique for
   * each of the 41 faces would walk the same data 41 times over.
   */
  const diceByFace: Record<string, Array<string>> = {};
  Object.keys(dieCollection).forEach((dieName) => {
    const seen: Array<string> = [];
    dieCollection[dieName].faces.forEach((group) => {
      group.forEach((face) => {
        if (!seen.includes(face)) {
          seen.push(face);
        }
      });
    });

    seen.forEach((face) => {
      diceByFace[face] = diceByFace[face] ?? [];
      diceByFace[face].push(dieName);
    });
  });

  const techniquesByFace: Record<string, number> = {};
  techniques.forEach((technique) => {
    // A recipe can call for the same face twice; it still only counts once.
    const counted: Array<string> = [];
    technique.ingredients.forEach((face) => {
      if (counted.includes(face)) {
        return;
      }
      counted.push(face);
      techniquesByFace[face] = (techniquesByFace[face] ?? 0) + 1;
    });
  });
</script>

<script lang="ts">
  import Icon from '../../icon/icon.svelte';
  import { glossary } from '../../../store';
  import type { FaceDefinition } from '../../models/face.type';

  interface Props {
    data: FaceDefinition;
  }

  let { data }: Props = $props();

  // DieFace.Blank is the empty string, so this row has no id to show and the
  // section's search can never match it.
  let title = $derived(data.id || 'Blank');

  let dice = $derived(diceByFace[data.id] ?? []);
  let techniqueCount = $derived(techniquesByFace[data.id] ?? 0);

  /**
   * Blank and Broken sit on 61 of the 62 dice, which is two solid rows of chips
   * for a fact the count already told you. Most faces are well under the cap and
   * show in full.
   */
  const CHIP_LIMIT = 12;
  let expanded = $state(false);
  let visibleDice = $derived(expanded ? dice : dice.slice(0, CHIP_LIMIT));

  /**
   * Descriptions mark up inline symbols as `<:sword>`. Nothing in the codebase
   * consumes that token -- <Text> looks for a bare `<sword>` and mangles this
   * one -- so the four faces that have a description were printing the raw
   * markup on screen. The capture group keeps the delimiters in the split, so
   * the pieces can be walked in order.
   */
  let parts = $derived(data.text ? data.text.split(/(<:[a-z-]+>)/gi) : []);

  const isIcon = (part: string) => /^<:[a-z-]+>$/i.test(part);
  const iconName = (part: string) => part.slice(2, -1);

  function openDie(dieName: string) {
    glossary.selected.set('die');
    glossary.searchTerm.set(dieName);
  }
</script>

<div class="container entry">
  <div class="icon">
    <Icon name={data.icon} />
  </div>

  <div class="information">
    <div class="heading">
      <h2>{title}</h2>

      {#if data.tags?.length}
        <div class="tags">
          {#each data.tags as tag}
            <span class="tag">{tag}</span>
          {/each}
        </div>
      {/if}
    </div>

    {#if parts.length}
      <p class="text">
        {#each parts as part}
          {#if isIcon(part)}
            <Icon name={iconName(part)} class="inline-icon" />
          {:else}{part}{/if}
        {/each}
      </p>
    {:else}
      <p class="text missing">No description written yet.</p>
    {/if}

    <div class="usage">
      <span class="count">
        <b>{dice.length}</b>
        {dice.length === 1 ? 'die' : 'dice'}
      </span>
      <span class="count">
        <b>{techniqueCount}</b>
        {techniqueCount === 1 ? 'technique' : 'techniques'}
      </span>
    </div>

    {#if dice.length}
      <div class="dice">
        {#each visibleDice as dieName}
          <button class="die-chip" onclick={() => openDie(dieName)}>{dieName}</button>
        {/each}

        {#if dice.length > CHIP_LIMIT}
          <button class="more" onclick={() => (expanded = !expanded)}>
            {expanded ? 'Show fewer' : `+${dice.length - CHIP_LIMIT} more`}
          </button>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 14px;
    align-items: start;

    text-align: left;
  }

  // Framed like a portrait so an icon-led row and a portrait-led row read as
  // the same kind of thing now that neither sits in a card of its own.
  .icon {
    .glossary-plate(64px);
    --icon-size: 38px;

    @media @mobile {
      .glossary-plate(48px);
      --icon-size: 28px;
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    grid-gap: 6px;
    min-width: 0;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    // Left-packed, not space-between: without a card edge to push against, a
    // right-aligned chip just floats off on its own halfway across the page.
    justify-content: flex-start;
    grid-gap: 8px;

    h2 {
      font-size: 1.15rem;
      text-transform: capitalize;
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 4px;

    .tag {
      padding: 2px 9px;
      font-size: 0.6rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      opacity: 0.7;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;
    }
  }

  .text {
    font-size: 0.9rem;
    line-height: 1.5;
    opacity: 0.85;

    // The inline symbols sit on the text baseline rather than stretching to the
    // 60px the icon column uses.
    :global(.inline-icon) {
      --icon-size: 1.1em;
      vertical-align: -0.15em;
    }

    // 37 of the 41 faces have no text yet, so this is the common case -- muted
    // enough not to shout, present enough to show what is left to write.
    &.missing {
      font-style: italic;
      opacity: 0.4;
    }
  }

  .usage {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 12px;
    margin-top: 2px;

    .count {
      font-size: 0.7rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      opacity: 0.6;

      b {
        font-size: 0.85rem;
        opacity: 1;
      }
    }
  }

  .dice {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 4px;

    .die-chip {
      // Reset of the global button.less pill.
      padding: 2px 9px;
      font-size: 0.7rem;
      text-transform: capitalize;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;
      box-shadow: none;
      opacity: 0.75;
      transition:
        opacity 0.2s,
        border-color 0.2s,
        color 0.2s;

      &:hover,
      &:focus-visible {
        filter: none;
        opacity: 1;
        color: @edge-shine;
        border-color: @edge-warm;
      }

      &:active {
        box-shadow: none;
      }
    }

    .more {
      padding: 2px 9px;
      font-size: 0.7rem;
      font-style: italic;
      background: none;
      border: 1px dashed var(--background-colour-light);
      border-radius: 999px;
      box-shadow: none;
      opacity: 0.6;

      &:hover,
      &:focus-visible {
        filter: none;
        opacity: 1;
      }

      &:active {
        box-shadow: none;
      }
    }
  }
</style>
